import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHeart, FiUsers, FiTarget, FiSun, FiChevronDown, FiChevronUp, FiCheck, FiExternalLink } = FiIcons;

function Principles() {
  const [expandedPrinciple, setExpandedPrinciple] = useState(0);
  const [expandedItems, setExpandedItems] = useState({});

  const principles = [
    {
      icon: FiHeart,
      title: "Modet att vara människa",
      subtitle: "Transparens och sårbarhet",
      color: "from-pink-500 to-rose-500",
      slug: "transparens",
      description: "I en värld som ständigt förändras är det lätt att känna pressen att alltid ha svaret. Men vad händer om vi istället omfamnar osäkerheten och ser sårbarhet som en styrka?",
      items: [
        {
          title: "Det är okej att säga 'jag vet inte'",
          description: "Att erkänna att man inte har alla svaren är inte ett tecken på inkompetens, utan på ärlighet och intellektuell ödmjukhet. Detta skapar en grund för psykologisk trygghet och stimulerar innovation."
        },
        {
          title: "Det är okej att be om ett förtydligande",
          description: "I kommunikation uppstår ofta missförstånd. Att be om ett förtydligande är ett proaktivt steg för att säkerställa gemensam förståelse och förhindra fel."
        },
        {
          title: "Det är okej att säga att man inte förstår",
          description: "Att erkänna att man inte förstår är en signal om mognad och självkännedom. Detta stimulerar djupare lärande och gemensam problemlösning."
        },
        {
          title: "Det är okej att fråga vad akronymer står för",
          description: "Att modigt fråga vad en akronym står för är inte bara en tjänst för en själv, utan ofta också för andra som sitter tysta med samma fråga."
        },
        {
          title: "Det är okej att göra misstag",
          description: "Misstag är oundvikliga delar av lärandeprocessen. I en fruktbar arbetsmiljö ses misstag som värdefulla möjligheter till insikt och utveckling."
        },
        {
          title: "Det är okej att presentera sig",
          description: "Att våga ta initiativet att presentera sig är en grundläggande byggsten för nätverkande och att skapa inkludering."
        }
      ]
    },
    {
      icon: FiUsers,
      title: "Kraften i gemenskap",
      subtitle: "Tillit, samarbete och delad framgång",
      color: "from-blue-500 to-indigo-500",
      slug: "gemenskap",
      description: "En arbetsplats är mer än bara en samling individer. Det är ett ekosystem där relationer och samverkan är avgörande för framgång.",
      items: [
        {
          title: "Det är okej att förlita sig på teamet",
          description: "Ingen individ har alla svaren, all energi eller all tid. En effektiv arbetsplats bygger på insikten att den kollektiva intelligensen överstiger den enskilda individens."
        },
        {
          title: "Det är okej att be om hjälp",
          description: "Att be om hjälp är ofta svårt, men i en sund kultur är det ett tecken på styrka, självkännedom och en vilja att uppnå bästa möjliga resultat."
        },
        {
          title: "Det är okej att ge feedback på andras arbete",
          description: "Feedback är den livsnerv som driver förbättring och utveckling. Konstruktiv feedback är en avgörande del av ett växande team."
        },
        {
          title: "Det är okej att ha högljudda dagar",
          description: "Spontanitet, skratt och informella samtal är vitala för att bygga teamanda och en känsla av gemenskap."
        },
        {
          title: "Det är okej att tacka ja när någon hämtar kaffe",
          description: "De små gesterna av vänlighet och gemenskap bygger ofta de starkaste banden och stärker den sociala väven på arbetsplatsen."
        },
        {
          title: "Det är okej att gå fram och fråga någon ansikte mot ansikte",
          description: "Ibland är det mest effektiva och mänskliga sättet att kommunicera att helt enkelt gå fram till en kollega och prata."
        }
      ]
    },
    {
      icon: FiTarget,
      title: "Balansens konst",
      subtitle: "Energi, återhämtning och personliga gränser",
      color: "from-green-500 to-emerald-500",
      slug: "balans",
      description: "Människan är inte en maskin. Vi behöver vila, återhämtning och tydliga gränser för att kunna prestera på topp och framför allt för att må bra.",
      items: [
        {
          title: "Det är okej att stanna hemma när man är sjuk",
          description: "Att komma till jobbet sjuk leder inte bara till smittspridning utan sänder också en signal till kollegor att de förväntas göra detsamma."
        },
        {
          title: "Det är okej att inte kolla mejlen utanför arbetstid",
          description: "Den ständiga uppkopplingen kan sudda ut gränserna mellan arbete och fritid, vilket leder till utbrändhet och stress."
        },
        {
          title: "Det är okej att inte ständigt kolla mejlen under arbetstid",
          description: "Att aktivt välja bort att ständigt övervaka sin mejl och istället schemalägga specifika tider är ett effektivt sätt att återta kontrollen över sin uppmärksamhet."
        },
        {
          title: "Det är okej att säga 'Nej' när man har för mycket att göra",
          description: "Att medvetet säga 'nej' till nya uppgifter när arbetsbördan redan är hög är ett tecken på realistisk självkännedom och förmåga att prioritera."
        },
        {
          title: "Det är okej att ha dåliga dagar",
          description: "Att acceptera att det är okej att ha dåliga dagar och att uttrycka det på ett lämpligt sätt är avgörande för en human arbetsplats."
        },
        {
          title: "Det är okej att ta ledigt",
          description: "Att ta ledigt är inte en lyx, utan en nödvändighet för återhämtning, personlig utveckling och att upprätthålla ett hållbart arbetsliv."
        }
      ]
    },
    {
      icon: FiSun,
      title: "Din plats, dina spelregler",
      subtitle: "Autonomi, fokus och individualitet",
      color: "from-amber-500 to-orange-500",
      slug: "autonomi",
      description: "Varje individ är unik, med egna preferenser för hur de arbetar bäst. Att erkänna och respektera denna individualitet är nyckeln till att låsa upp potential.",
      items: [
        {
          title: "Det är okej att ha tysta dagar",
          description: "Vissa dagar kräver djup koncentration och ostördhet. Att respektera behovet av tysta dagar är grundläggande för att möjliggöra djuparbete."
        },
        {
          title: "Det är okej att sätta på sig hörlurarna",
          description: "Hörlurar har blivit en universell symbol för 'stör ej'. Det är en enkel men effektiv strategi för att skapa sin egen zon."
        },
        {
          title: "Det är okej att gå någon annanstans för att koncentrera sig",
          description: "För vissa uppgifter kan en miljöomväxling vara avgörande för att uppnå optimal koncentration."
        },
        {
          title: "Det är okej att småäta",
          description: "Energinivåerna fluktuerar under dagen. Att tillåta småätande är ett sätt att erkänna kroppens behov och bibehålla en jämn energinivå."
        },
        {
          title: "Det är okej att ha ett stökigt skrivbord",
          description: "För vissa är en viss oordning ett tecken på kreativitet och en process för att organisera tankar snarare än föremål."
        },
        {
          title: "Det är okej att arbeta på det sätt man själv vill",
          description: "Så länge resultaten levereras bör individen ha frihet att välja sina egna metoder och arbetssätt."
        }
      ]
    }
  ];

  const toggleItem = (principleIndex, itemIndex) => {
    const key = `${principleIndex}-${itemIndex}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="py-12">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Fyra <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">principer</span> för en human arbetsplats
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              Upptäck de grundläggande principerna som skapar psykologisk trygghet, 
              bygger tillit och främjar innovation på din arbetsplats.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Principles Navigation */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-4 gap-4">
            {principles.map((principle, index) => (
              <motion.button
                key={index}
                onClick={() => setExpandedPrinciple(index)}
                className={`p-4 rounded-xl text-left transition-all duration-300 ${
                  expandedPrinciple === index
                    ? 'bg-white shadow-lg border-2 border-blue-200'
                    : 'bg-white/70 hover:bg-white hover:shadow-md'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${principle.color} flex items-center justify-center mb-3`}>
                  <SafeIcon icon={principle.icon} className="text-white text-lg" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">{principle.title}</h3>
                <p className="text-xs text-gray-600">{principle.subtitle}</p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Active Principle Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={expandedPrinciple}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="text-center mb-12">
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${principles[expandedPrinciple].color} flex items-center justify-center mx-auto mb-6`}>
                  <SafeIcon icon={principles[expandedPrinciple].icon} className="text-white text-3xl" />
                </div>
                
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {principles[expandedPrinciple].title}
                </h2>
                
                <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-6">
                  {principles[expandedPrinciple].description}
                </p>

                {/* Deep dive links */}
                <div className="flex justify-center space-x-4">
                  <Link
                    to="/forskning"
                    className="inline-flex items-center px-4 py-2 text-sm border border-blue-600 text-blue-600 rounded-full hover:bg-blue-50 transition-colors"
                  >
                    Se forskning
                    <SafeIcon icon={FiExternalLink} className="ml-1 text-xs" />
                  </Link>
                  
                  <Link
                    to="/framgang"
                    className="inline-flex items-center px-4 py-2 text-sm border border-green-600 text-green-600 rounded-full hover:bg-green-50 transition-colors"
                  >
                    Framgångshistorier
                    <SafeIcon icon={FiExternalLink} className="ml-1 text-xs" />
                  </Link>
                </div>
              </div>

              <div className="space-y-4">
                {principles[expandedPrinciple].items.map((item, itemIndex) => {
                  const itemKey = `${expandedPrinciple}-${itemIndex}`;
                  const isExpanded = expandedItems[itemKey];
                  
                  return (
                    <motion.div
                      key={itemIndex}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: itemIndex * 0.1 }}
                      className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
                    >
                      <button
                        onClick={() => toggleItem(expandedPrinciple, itemIndex)}
                        className="w-full p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                              <SafeIcon icon={FiCheck} className="text-green-600 text-sm" />
                            </div>
                            <h3 className="font-semibold text-gray-900 text-lg">
                              {item.title}
                            </h3>
                          </div>
                          
                          <SafeIcon 
                            icon={isExpanded ? FiChevronUp : FiChevronDown} 
                            className="text-gray-400 text-xl flex-shrink-0"
                          />
                        </div>
                      </button>
                      
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="px-6 pb-6">
                              <div className="border-t border-gray-100 pt-4">
                                <p className="text-gray-700 leading-relaxed">
                                  {item.description}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Implementation Tips */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Börja implementera idag
            </h2>
            
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Varje "det är okej att..." du implementerar är ett steg mot en mer human, 
              effektiv och innovativ arbetsplats. Börja smått och bygg kulturen steg för steg.
            </p>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Nästa steg
              </h3>
              
              <p className="text-gray-700 mb-6">
                Välj en princip som resonerar mest med dig och börja där. 
                Små förändringar kan skapa stora vågor i teamdynamiken.
              </p>
              
              <div className="flex justify-center space-x-8 mb-6">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <p className="text-sm text-gray-600">Välj en princip</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                    <span className="text-green-600 font-bold">2</span>
                  </div>
                  <p className="text-sm text-gray-600">Implementera gradvis</p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
                    <span className="text-purple-600 font-bold">3</span>
                  </div>
                  <p className="text-sm text-gray-600">Bygg kulturen</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/assessment"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-green-600 text-white font-semibold rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                >
                  Gör kulturassessment
                </Link>
                
                <Link
                  to="/resurser"
                  className="inline-flex items-center px-6 py-3 border-2 border-blue-600 text-blue-600 font-semibold rounded-full hover:bg-blue-50 transition-all duration-200"
                >
                  Se implementeringsresurser
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Principles;